import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, apiClient } from '../../api/client';
import Button from '../../components/ui/Button';
import PasswordInput from '../../components/PasswordInput';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

const OrganizationCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingUser, setExistingUser] = useState<{ name: string } | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Debounced email check
  const checkEmailExists = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) {
      setExistingUser(null);
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await apiClient.get<{ exists: boolean; user: { name: string } | null }>(`/api/super-admin/users/check-email?email=${encodeURIComponent(email)}`);
      setExistingUser(response.data.exists ? response.data.user : null);
    } catch {
      setExistingUser(null);
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkEmailExists(ownerEmail);
    }, 500);
    return () => clearTimeout(timer);
  }, [ownerEmail, checkEmailExists]);

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value));
    }
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload: Record<string, string> = {
        name,
        slug,
        contactEmail,
        ownerName,
        ownerEmail,
      };
      // Only send password if user is new
      if (!existingUser && ownerPassword) {
        payload.ownerPassword = ownerPassword;
      }
      await api.superAdmin.organizations.create(payload);
      navigate('/super-admin/organizations');
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-form-page">
      <h2>{t('superAdmin.organizations.createTitle')}</h2>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <form onSubmit={submitHandler} className="form-container">
        <fieldset className="form-fieldset">
          <legend>{t('superAdmin.organizations.orgDetails')}</legend>

          <div className="form-group">
            <label htmlFor="name">{t('common.name')}</label>
            <input
              type="text"
              id="name"
              placeholder={t('superAdmin.organizations.enterName')}
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">{t('superAdmin.organizations.slug')}</label>
            <input
              type="text"
              id="slug"
              placeholder={t('superAdmin.organizations.enterSlug')}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
            />
            <small className="form-help">{t('superAdmin.organizations.slugHelp')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="contactEmail">{t('superAdmin.organizations.contactEmail')}</label>
            <input
              type="email"
              id="contactEmail"
              placeholder={t('superAdmin.organizations.enterContactEmail')}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>

        </fieldset>

        <fieldset className="form-fieldset">
          <legend>{t('superAdmin.organizations.ownerDetails')}</legend>

          <div className="form-group">
            <label htmlFor="ownerName">{t('common.name')}</label>
            <input
              type="text"
              id="ownerName"
              placeholder={t('superAdmin.organizations.enterOwnerName')}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="ownerEmail">{t('common.email')}</label>
            <input
              type="email"
              id="ownerEmail"
              placeholder={t('superAdmin.organizations.enterOwnerEmail')}
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              required
            />
            {checkingEmail && (
              <small className="form-help">{t('common.checking')}...</small>
            )}
            {existingUser && !checkingEmail && (
              <small className="form-help" style={{ color: 'var(--success)' }}>
                {t('superAdmin.organizations.existingUserFound', { name: existingUser.name })}
              </small>
            )}
          </div>

          {!existingUser && (
            <div className="form-group">
              <label htmlFor="ownerPassword">{t('common.password')}</label>
              <PasswordInput
                id="ownerPassword"
                placeholder={t('superAdmin.organizations.enterOwnerPassword')}
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                required={!existingUser}
                minLength={8}
              />
            </div>
          )}
        </fieldset>

        <Button type="submit" fullWidth loading={loading}>
          {loading ? t('common.creating') : t('superAdmin.organizations.createButton')}
        </Button>
      </form>

      <Link to="/super-admin/organizations" className="form-link">
        {t('superAdmin.organizations.goBack')}
      </Link>
    </div>
  );
};

export default OrganizationCreatePage;
